<script setup>
  import CountUp from 'vue-countup-v3'
  import getFullName from '~/utils/get-full-name'

  const { href } = useRequestURL()
  const { hash, params, path, query } = useRoute()
  const { user } = useUser()
  const { find } = useStrapi4()
  const client = useStrapiClient()

  const applyModal = ref(null)
  const openApplyModal = () => {
    applyModal.value.open()
  }

  const employer = reactive({
    ...(await useAsyncData(`employeur-${params.slug}`, async () => {
      const options = {
        params: {
          fields: ['acronym', 'description', 'name', 'slug', 'website', 'defaultCrushes'],
          populate: {
            cover: { fields: ['url'] },
            logo: { fields: ['url'] },
            networks: { fields: ['facebook', 'twitter'] },
            page: {
              fields: ['chartUrl', 'projectsTitle', 'territoryTitle'],
              populate: {
                chartFile: { fields: 'url' },
                keyFigures: { fields: ['label', 'number', 'unit'] },
                projects: {
                  fields: ['description', 'video'],
                  populate: { photo: { fields: ['url'] } }
                },
                team: {
                  fields: ['firstName', 'lastName', 'linkedin', 'title', 'info1'],
                  populate: { picture: { fields: ['url'] } }
                },
                territoryPhotos: {
                  fields: ['legend'],
                  populate: {
                    file: { fields: ['url'] }
                  }
                }
              }
            },
            locations: { fields: ['city', 'postalCode', 'street'] },
            seo: {
              fields: ['metaDescription', 'metaTitle', 'metaRobots', 'structuredData'],
              populate: {
                metaImage: { fields: ['url'] }
              }
            },
            type: { fields: ['name', 'slug'] },
            children: {
              fields: ['name', 'slug'],
              populate: {
                cover: { fields: ['url'] },
                logo: { fields: ['url'] }
              }
            },
            parent: {
              fields: ['name', 'slug'],
              populate: {
                cover: { fields: ['url'] },
                logo: { fields: ['url'] }
              }
            }
          }
        }
      }
      if (query.preview?.length) options.query = { preview: query.preview }
      const res = await client(`/employers/${params.slug}`, options)
      return res.data
    }))
  })

  if (employer.error) {
    throw createError({ statusCode: 404, statusMessage: 'Employer not found', url: path, fatal: true })
  }

  const keyFigures = computed(() => employer.data?.page?.keyFigures ?? [])
  const pickedKeyFigure = ref(null)

  let emp
  let jobs
  if (employer.data) {
    emp = employer.data

    const referrer = useReferrer()
    if (process.client) {
      const originUrl = sessionStorage.getItem(`employer:${emp.id}`)
      if (!originUrl && referrer.value) sessionStorage.setItem(`employer:${emp.id}`, referrer.value)
      referrer.value = null
    }

    if (process.client && !('preview' in query)) {
      client(`/employers/${emp.id}/view`, {
        body: { url: href },
        method: 'POST'
      }).catch(error => handleError(error))
    }

    jobs = reactive({
      ...useLazyAsyncData(
        `employer-${emp.id}-jobs`,
        async () => {
          const res = await find('jobs', {
            fields: ['moderated', 'slug', 'title'],
            filters: {
              employer: emp.id,
              published: true
            },
            pagination: { limit: 100, start: 0, withCount: false },
            populate: {
              employer: {
                fields: ['name'],
                populate: {
                  cover: { fields: ['url'] },
                  logo: { fields: ['url'] }
                }
              },
              header: {
                fields: ['title'],
                populate: {
                  cover: { fields: ['url'] }
                }
              },
              speaker: {
                fields: ['firstName', 'lastName'],
                populate: {
                  photo: { fields: ['url'] }
                }
              },
              favorites: {
                fields: ['favorites'],
                populate: {
                  user: { fields: ['username'] }
                }
              }
            },
            sort: { validatedAt: 'desc' }
          })
          return res.data
        },
        { default: () => [] }
      )
    })

    const seo = emp.seo ?? {}
    useSeo({
      metaTitle: seo.metaTitle ?? emp.name,
      metaDescription: seo.metaDescription ?? emp.description,
      metaRobots: seo.metaRobots,
      structuredData: seo.structuredData
    })

    useHead({
      meta: [{ hid: 'og:image', property: 'og:image', content: useStrapiMedia(seo.metaImage?.url ?? emp.cover?.url) }]
    })
  }

  const createAlert = ref(false)

  onMounted(() => {
    if (keyFigures.value.length < 2) return
    pickedKeyFigure.value = keyFigures.value[1]
    let index
    setInterval(() => {
      index = index < keyFigures.value.length - 1 ? index + 1 : 1
      pickedKeyFigure.value = keyFigures.value[index]
    }, 5000)
  })
</script>

<template>
  <Loading class="mx-auto max-w-screen-xl px-4 py-16" v-if="employer?.pending" />
  <div class="bg-zinc-100" v-else-if="employer?.data">
    <div
      class="h-60 overflow-x-hidden bg-cover bg-center sm:h-80"
      :style="{
        backgroundImage: `url('${useStrapiMedia(employer.data.cover?.url)}')`
      }"
    >
      <div class="h-full bg-black/50">
        <div class="mx-auto flex h-full max-w-screen-xl flex-col items-center justify-center space-y-4 px-4 text-center">
          <h1 class="line-clamp-3 font-special text-2xl font-bold text-white sm:text-3xl">{{ employer.data.name }}</h1>
          <h2 class="!mt-3 font-special text-xl font-bold uppercase text-white" v-if="employer.data.acronym">{{ employer.data.acronym }}</h2>
          <ul class="-mx-2 -my-0.5 flex flex-wrap items-center justify-center text-white">
            <li class="mx-2 my-0.5 space-x-1.5" v-if="employer.data.type?.name">
              <FontAwesomeIcon class="text-viking-400" icon="fa-solid fa-tag" />
              <span>{{ employer.data.type.name }}</span>
            </li>
            <li class="mx-2 my-0.5 space-x-1.5" v-if="employer.data.locations?.length">
              <FontAwesomeIcon class="text-viking-400" icon="fa-solid fa-location-dot" />
              <span
                ><Tooltip
                  :content="employer.data.locations.map(p => [p.street, p.postalCode, p.city].filter(Boolean).join(', ')).join(' / ')"
                  placement="bottom"
                >
                  <span class="underline">
                    {{
                      employer.data.locations[0].city +
                      (employer.data.locations.length > 1 ? ', ' + employer.data.locations[1].city.substring(0, 3) + '...' : '')
                    }}
                  </span>
                </Tooltip>
              </span>
            </li>
            <li class="mx-2 my-0.5 space-x-1.5" v-if="employer.data.website">
              <FontAwesomeIcon class="text-viking-400" icon="fa-solid fa-link" />
              <NuxtLink target="_blank" :to="employer.data.website">{{
                employer.data.website.replace('https://', '').replace('http://', '').replace('www.', '')
              }}</NuxtLink>
            </li>
          </ul>
          <div
            class="w-[10%] rounded-full bg-cover bg-center pt-[10%]"
            v-if="employer.data.logo?.url"
            :style="{
              backgroundImage: `url('${useStrapiMedia(employer.data.logo.url)}')`
            }"
          />
        </div>
      </div>
    </div>

    <div class="sticky top-16 z-10 bg-tarawera-900 shadow">
      <div class="mx-auto max-w-screen-xl px-4 py-4 text-white">
        <div class="flex flex-wrap items-center justify-end">
          <Button v-if="!user || user.candidate" @click="openApplyModal">Candidature spontanée</Button>
          <Tooltip v-else placement="top">
            <Button class="pointer-events-none opacity-50">Candidature spontanée</Button>
            <template #content><span class="text-tarawera-900">Vous n’avez pas d’espace candidat</span></template>
          </Tooltip>
        </div>
      </div>
    </div>

    <div class="space-y-12 pb-8 md:space-y-16 md:pb-12">
      <section class="pt-8 md:pt-12">
        <Quote class="mx-auto max-w-screen-lg px-4 text-lg">
          <div v-html="employer?.data.description" />
        </Quote>
      </section>

      <section class="relative bg-zinc-100" v-if="jobs.data?.length">
        <div class="mx-auto max-w-screen-xl px-4">
          <Loading v-if="jobs.pending" />
          <Error v-else-if="jobs.error" :retry="jobs.refresh" />

          <div class="space-y-8" v-else>
            <div class="text-center font-special text-xl font-bold">Nos offres</div>
            <Swiper class="-mx-4">
              <div class="max-w-full flex-none snap-center px-4" v-for="job in jobs.data" :key="job.id">
                <CardJob class="my-2 w-80 max-w-full" :job="job" />
              </div>
            </Swiper>
          </div>
        </div>
      </section>

      <section class="relative mx-auto max-w-screen-xl px-4" v-if="employer.data.page?.team?.length">
        <div class="text-center font-special text-xl font-bold">Notre équipe</div>
        <div class="mt-4 flex justify-center space-x-2 text-center font-special font-bold">
          <div class="" v-if="employer.data.page?.chartUrl || employer.data.page?.chartFile?.url">
            <NuxtLink
              class="font-special text-sm font-bold underline decoration-viking-400 decoration-2 underline-offset-4 duration-300 hover:text-viking-500"
              target="_blank"
              :to="employer.data.page?.chartUrl || useStrapiMedia(employer.data.page?.chartFile?.url)"
              >Voir l'organigramme</NuxtLink
            >
          </div>
          <div v-if="employer.data.page?.keyFigures[0]">
            {{ employer.data.page?.keyFigures[0].number }} {{ employer.data.page?.keyFigures[0].label }}
          </div>
        </div>

        <Swiper class="-mx-4 mt-8">
          <div
            class="flex w-1/2 flex-none snap-align-none items-baseline justify-center px-4 md:w-1/4"
            v-for="member in employer.data.page?.team"
            :key="member.id"
          >
            <Profile
              :profile="{
                linkedin: member.linkedin,
                name: getFullName(member, false),
                photo: member.picture?.url ? useStrapiMedia(member.picture.url) : undefined,
                title: member.title
              }"
              :photoSize="20"
            />
          </div>
        </Swiper>
      </section>

      <section
        class="relative flex flex-col items-center justify-evenly space-y-12 bg-tarawera-900 px-4 py-8 text-white md:flex-row md:space-y-0 md:px-16 md:py-12"
        v-if="employer.data.defaultCrushes"
      >
        <div class="w-full text-center font-bold md:w-1/2" v-if="pickedKeyFigure">
          <div class="flex justify-center space-x-2 font-special text-5xl lg:text-6xl">
            <ClientOnly>
              <CountUp :end-val="pickedKeyFigure.number || 0" duration="1" :options="{ separator: '' }" />
              <template #fallback>
                <span>{{ pickedKeyFigure.number || 0 }}</span>
              </template>
            </ClientOnly>
            <span>{{ pickedKeyFigure.unit }}</span>
          </div>
          <div class="text-xl">{{ pickedKeyFigure.label }}</div>
        </div>
        <div class="flex w-full items-center md:w-1/2">
          <div class="relative mx-auto max-w-sm rounded-md bg-zinc-100 p-4 text-tarawera-900" v-if="employer.data.defaultCrushes">
            <NuxtImg class="absolute -top-4 right-2 h-16 w-16 rounded-full sm:-right-4" src="/images/logo-pp-400x400.webp" alt="Logo Profil Public" />
            <div class="font-special text-lg font-bold">Les petits +</div>
            <div class="text-zinc-500">dénichés par Profil Public</div>
            <ul class="mt-4 space-y-2">
              <li v-for="(item, index) in employer.data.defaultCrushes?.trim().split('\n').slice(0, 3)" v-text="item" :key="index" />
            </ul>
          </div>
        </div>
      </section>

      <section class="relative mx-auto max-w-screen-xl" v-if="employer.data.page?.projects?.length">
        <div
          class="mb-8 text-center font-special text-xl font-bold"
          v-if="employer.data.page?.projectsTitle"
          v-text="employer.data.page?.projectsTitle"
        />
        <Swiper :controller="false">
          <div class="w-full max-w-full flex-none snap-center px-4" v-for="project in employer.data.page?.projects" :key="project.id">
            <div class="flex flex-col items-center justify-center space-y-4 md:flex-row md:space-x-8">
              <div class="w-full md:w-1/2" v-if="project.video || project.photo?.url">
                <div class="relative pt-[56.25%]" v-if="project.video">
                  <iframe
                    class="absolute inset-0 h-full w-full"
                    :src="project.video"
                    title="YouTube video player"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  />
                </div>
                <div
                  class="w-full bg-cover bg-center pt-[56.25%]"
                  v-else
                  :style="{
                    backgroundImage: `url('${useStrapiMedia(project.photo.url)}')`
                  }"
                />
              </div>
              <div class="p-2 font-special font-bold md:w-1/2" v-html="project.description" />
            </div>
          </div>
          <template v-slot:end="{ arrivedState, progress, slide }">
            <nav class="mx-auto mt-8 flex max-w-lg items-center space-x-4 px-4">
              <button
                class="text-tarawera-800 duration-300 hover:text-tarawera-900 disabled:opacity-50"
                :disabled="arrivedState.left"
                @click="slide('left')"
              >
                <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
              </button>
              <div class="-mt-0.5 h-2 flex-1 overflow-hidden rounded-full bg-zinc-300">
                <div class="h-full rounded-full bg-gradient-to-r from-tarawera-800 to-tarawera-900" :style="{ width: `${progress}%` }"></div>
              </div>
              <button
                class="text-tarawera-800 duration-300 hover:text-tarawera-900 disabled:opacity-50"
                :disabled="arrivedState.right"
                @click="slide('right')"
              >
                <FontAwesomeIcon icon="fa-solid fa-chevron-right" />
              </button>
            </nav>
          </template>
        </Swiper>
      </section>

      <section class="mx-auto max-w-screen-xl px-4" v-if="employer.data.page?.territoryPhotos?.length">
        <div
          class="mb-8 text-center font-special text-xl font-bold"
          v-if="employer.data.page?.territoryTitle"
          v-text="employer.data.page?.territoryTitle"
        />
        <Swiper class="-mx-4">
          <div class="max-w-full snap-center px-4 sm:snap-align-none" v-for="photo in employer.data.page?.territoryPhotos" :key="photo.id">
            <div class="flex w-[28rem] max-w-full flex-col items-center">
              <div
                class="w-full bg-cover bg-center pt-[56.25%]"
                :style="{
                  backgroundImage: photo.file ? `url('${useStrapiMedia(photo.file.url)}')` : undefined
                }"
              />
              <div class="mt-2 text-center" v-text="photo.legend" />
            </div>
          </div>
        </Swiper>
      </section>

      <section class="mx-auto max-w-screen-xl px-4" v-if="employer.data.children?.length || employer.data.parent">
        <div class="mb-8 text-center font-special text-xl font-bold">Nous recrutons également ici</div>
        <div class="mx-auto w-1/3" v-if="employer.data.parent">
          <CardEmployer :employer="employer.data.parent" />
        </div>
        <Swiper class="-mx-4">
          <div class="max-w-full snap-center px-4 sm:snap-align-none" v-for="child in employer.data.children" :key="child.id">
            <CardEmployer class="my-1.5 w-80 max-w-full" :employer="child" />
          </div>
        </Swiper>
      </section>

      <section class="mx-auto max-w-screen-xl px-4">
        <div class="flex flex-col items-center justify-center font-special text-lg font-bold sm:flex-row">
          <p class="text-sm sm:text-base">Vous voulez être notifié de nos prochaines offres ?</p>
          <Button class="mx-3 my-2" size="sm" @click=""><Icon class="mr-2" name="bell" type="solid" />Être alerté</Button>
        </div>
      </section>
    </div>

    <ClientOnly>
      <PagesJobsJobAlertDialog
        v-if="user?.candidate"
        :filters="{ search: employer.data.name, employerType: [employer.data.type] }"
        :name="employer.data.name"
        :open="createAlert"
        @close="createAlert = false"
      >
        Une alerte vous permet de recevoir par email et à une fréquence choisie des nouvelles offres de l'employeur
        <strong class="uppercase">{{ employer.data.name }}</strong
        >.
      </PagesJobsJobAlertDialog>
    </ClientOnly>
    <ModalApply ref="applyModal" :employer="employer.data" :motivationLetterField="false" :messageField="true" />

    <LazyClientOnly>
      <PagesEmployeursPlacesAndSocialNetworks
        v-if="employer.data.networks && employer.data.places"
        :facebook="employer.data.networks.facebook"
        :places="employer.data.places"
        :twitter="employer.data.networks.twitter"
      />
    </LazyClientOnly>
  </div>
</template>
